import { createApp } from "vue";
import { createPinia } from "pinia";
import i18n from "components/jiffy/vue-cart/vue-i18n";
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";
import { useSpanglesStore } from "components/jiffy/vue-product-details-dtf/spangles-module";
import DesignLibraryModal from "./DesignLibraryModal.vue";

const CONTAINER_ID = "design-library-modal-container";
const MOBILE_PER_PAGE = 20;
const DESKTOP_PER_PAGE = 36;

class DesignLibraryModalManager {
  #app = null;

  #vm = null;

  #containerId = CONTAINER_ID;

  get #$container() {
    return document.getElementById(this.#containerId);
  }

  get #state() {
    const $container = this.#$container;
    return {
      businessLine: $container.dataset.businessLine,
      catalogNumber: $container.dataset.catalogNumber,
      singleSelectionMode: $container.dataset.singleSelectionMode === "true",
      pdpType: $container.dataset.pdpType,
      reorderEnabled: $container.dataset.reorderEnabled === "true"
    };
  }

  constructor() {
    const $container = this.#ensureContainer();

    const state = this.#state;

    this.#app = createApp(DesignLibraryModal, {
      index: state.index ?? 0,
      singleSelectionMode: !!state.singleSelectionMode,
      reorderEnabled: !!state.reorderEnabled
    });

    const pinia = createPinia();

    this.#app.use(pinia);
    this.#app.use(i18n);

    this.#vm = this.#app.mount($container);

    DesignLibraryModalManager.seedDtfStore(state);
    DesignLibraryModalManager.seedSpanglesStore(state);
  }

  #ensureContainer() {
    let $container = this.#$container;
    if (!$container) {
      $container = document.createElement("div");
      $container.id = this.#containerId;
      document.body.appendChild($container);
    }
    return $container;
  }

  static seedDtfStore(state = {}) {
    const store = useDtfStore();

    const stateMapping = {
      catalogNumber: store.setCatalogNumber,
      pdpType: store.setPdpType,
      puffMaxImageCount: store.setPuffMaxImageCount,
      businessLine: store.setBusinessLine,
      locale: store.setLocale,
      dtfChannelUuid: store.setDtfChannelUuid,
      sticker: value => store.setSticker(!!value),
      isPuff: value => store.setIsPuff(!!value),
      isSpangle: value => store.setIsSpangle(!!value),
      isStickerSample: value => store.setIsStickerSample(!!value),
      stickerQrScanned: value => store.setStickerQrScanned(!!value),
      isGoldFoil: value => store.setIsGoldFoil(!!value),
      printAsIs: value => store.setPrintAsIs(!!value)
    };

    Object.entries(stateMapping).forEach(([key, setter]) => {
      if (state[key] !== undefined) {
        setter(state[key]);
      }
    });

    store.setReorderEnabled(!!state.reorderEnabled);
    store.setIsMobile(!!state.isMobile);
    store.setArtworkHistory([]);
    store.setArtworkHistoryPagination({
      page: 1,
      perPage: state.isMobile ? MOBILE_PER_PAGE : DESKTOP_PER_PAGE
    });
  }

  static seedSpanglesStore(state) {
    if (state?.spanglesMaxImageCount === undefined) return;

    const spangles = useSpanglesStore();
    spangles.setSpanglesMaxImageCount?.(state.spanglesMaxImageCount);
  }
}

export default DesignLibraryModalManager;
